import React, { useCallback, useEffect, useState, FunctionComponent } from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Link, RouteComponentProps, useParams } from 'react-router-dom';

import {
  ExternalLink,
  FooterTeaser,
  FormGroup,
  Header,
  Section,
  SiteLogo,
  SiteName,
  SubHeader,
  Svg,
  TextboxField,
  TradingBlockLink,
  Loading,
  Text,
  FieldRequirementsHelp,
  isPasswordValid,
} from '@tradingblock/components';
import {
  GetSecurityChallengeUrl,
  GetUrlParams,
  PasswordChangeFinalizeUrl,
  PasswordChangeUrl,
  UserRequirementsUrl,
} from '@tradingblock/api';
import { config as Config } from '../clientConfig';
import {
  UserRequirementsInfo,
  SecurityQuestionType,
  ResponseCodes,
  FieldRequirementType,
  RequirementOverrides,
  FieldRequirementTypes,
  UserProfileLevel,
  Groups,
} from '@tradingblock/types';
import { getConfig, getEnvironment, getStaticPath } from '../utils';
import { LoginCardAuthFooter } from './LoginCardAuthFooter';

interface ResetPasswordConfirmationFormValues {
  password: string;
  passwordConfirm: string;
  securityAnswer?: string;
}

export const ResetPasswordConfirmation: FunctionComponent<RouteComponentProps> = ({ location }) => {
  const env = getEnvironment(location);
  const isVirtual = env === 'virtual';
  const config = getConfig(env);
  const apiRootUrl = config.authurl || undefined;

  const searchParams = GetUrlParams(location.search);
  const userLevel = searchParams['userLevel'];
  const loginResponseCode = Number.parseInt(searchParams['loginResponseCode']);

  const isExpiredOrAdminPasswordReset = loginResponseCode === 112 || loginResponseCode === 113;
  const isExpiredPasswordResetForAccountLevelUser = loginResponseCode === 113 && userLevel === UserProfileLevel.Account;
  const shouldShowSecurityQuestion = !(
    (loginResponseCode === 113 && userLevel !== UserProfileLevel.Account) ||
    loginResponseCode === 112
  );

  const grp = Config.siteGrp;
  const siteParam = grp ? `?grp=${grp}` : '';
  const group = grp === Groups.mb ? Groups.mb : Groups.tb;

  const defaultUserRequirementsInfo: UserRequirementsInfo = {
    nameRequirements: {
      minLength: 6,
      maxLength: 30,
      minDigitCount: 0,
      minSpecialCount: 0,
      acceptableSpecialChars: '~!#$=+._',
      minLowerCount: 0,
      minUpperCount: 0,
    },
    passwordRequirements: {
      minCharAllowed: '!',
      maxCharAllowed: '~',
      minLowerCount: 1,
      minUpperCount: 1,
      minDigitCount: 1,
      minSpecialCount: 1,
      minLength: 8,
      maxLength: 0,
      preferredMinLength: 12,
    },
  };

  const defaultRequirementOverrides: RequirementOverrides = {
    [FieldRequirementTypes.ContainsFirstName]: { requirementMet: undefined },
    [FieldRequirementTypes.ContainsLastName]: { requirementMet: undefined },
    [FieldRequirementTypes.ContainsUsername]: { requirementMet: undefined },
    [FieldRequirementTypes.RepeatedPassword]: { requirementMet: undefined },
  };

  const { token } = useParams();

  const [successfulSubmission, setSuccessfulSubmission] = useState<boolean>(false);
  const [currentSecurityQuestionType, setCurrentSecurityQuestionType] = useState<SecurityQuestionType | null>(null);
  const [error, setError] = useState<string>('');
  const [loadingSecurityQuestionType, setLoadingSecurityQuestionType] = useState<boolean>(false);
  const [submissionProcessing, setSubmissionProcessing] = useState<boolean>(false);
  const [pageLoadedWithJs, setPageLoadedWithJs] = useState<boolean>(false);
  const [loadingUserRequirements, setLoadingUserRequirements] = useState<boolean>(true);
  const [userRequirementsInfo, setUserRequirementsInfo] = useState<UserRequirementsInfo>(defaultUserRequirementsInfo);
  const [lastSubmittedPassword, setLastSubmittedPassword] = useState<string>('');
  const [lastSubmittedSecurityChallengeAnswer, setLastSubmittedSecurityChallengeAnswer] = useState<string>('');
  const [requirementOverrides, setRequirementOverrides] = useState<RequirementOverrides>(defaultRequirementOverrides);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [securityChallengeError, setSecurityChallengeError] = useState<string>('');

  const genericPasswordResetSubmissionErrorMsg =
    'There was an issue resetting your password. Please try again. If the issue persists please contact support.';
  const genericGetSecurityChallengeErrorMsg =
    'There was an issue loading the security question. Please try refreshing the page. If the issue persists please contact support.';
  const standardPasswordResetTokenExpiredMsg =
    'The token has expired. In order to generate a new one you must start the password reset process over.';
  const expiredOrTempPasswordResetTokenExpiredMsg =
    'The token has expired. In order to generate a new one you must attempt to log in again with your current password.';

  useEffect(() => {
    // If JavaScript is disabled, this useEffect won't be fired, and the form won't be rendered.
    setPageLoadedWithJs(true);

    if (shouldShowSecurityQuestion) {
      getSecurityChallenge();
    }

    fetch(`${apiRootUrl}/${UserRequirementsUrl}`)
      .then(res => {
        res
          .json()
          .then(resBody => {
            if (resBody.ResponseCode === 0) {
              const payload = resBody.Payload as UserRequirementsInfo;

              setUserRequirementsInfo(payload);
            }

            setLoadingUserRequirements(false);
          })
          .catch(() => {
            setLoadingUserRequirements(false);
          });
      })
      .catch(() => {
        setLoadingUserRequirements(false);
      });
  }, []);

  const getFieldRequirementTypeFromErrorMessage = (errorMsg: string): FieldRequirementType | '' => {
    switch (errorMsg) {
      case 'Password cannot contain your username':
        return FieldRequirementTypes.ContainsUsername;
      case 'Password cannot contain your first name':
        return FieldRequirementTypes.ContainsFirstName;
      case 'Password cannot contain your last name':
        return FieldRequirementTypes.ContainsLastName;
      case 'Cannot use the same password more than once':
        return FieldRequirementTypes.RepeatedPassword;
      default:
        return '';
    }
  };

  const getSecurityChallenge = useCallback(() => {
    setLoadingSecurityQuestionType(true);
    setCurrentSecurityQuestionType(null);

    if (token) {
      fetch(
        `${apiRootUrl}/${GetSecurityChallengeUrl}${
          isExpiredPasswordResetForAccountLevelUser ? '' : `?temptoken=${token}`
        }`,
        {
          ...(isExpiredPasswordResetForAccountLevelUser && { headers: { Authorization: token } }),
        }
      )
        .then(res => {
          if (res.status === 200) {
            res
              .json()
              .then(resBody => {
                if (resBody.ResponseCode === 0) {
                  setCurrentSecurityQuestionType(resBody.Payload);
                } else if (resBody.ResponseCode === 99) {
                  setError(genericGetSecurityChallengeErrorMsg);
                } else if (resBody.ResponseCode === 208) {
                  setError(`${ResponseCodes[resBody.ResponseCode].description} Please contact support.`);
                } else if (resBody.ResponseCode === 200) {
                  if (isExpiredOrAdminPasswordReset) {
                    setError(expiredOrTempPasswordResetTokenExpiredMsg);
                  } else {
                    setError(standardPasswordResetTokenExpiredMsg);
                  }
                } else {
                  setError(ResponseCodes[resBody.ResponseCode].description);
                }

                setLoadingSecurityQuestionType(false);
              })
              .catch(() => {
                setLoadingSecurityQuestionType(false);
                setError(genericGetSecurityChallengeErrorMsg);
              });
          } else {
            setLoadingSecurityQuestionType(false);
            setError(genericGetSecurityChallengeErrorMsg);
          }
        })
        .catch(() => {
          setLoadingSecurityQuestionType(false);
          setError(genericGetSecurityChallengeErrorMsg);
        });
    }
  }, [token]);

  // On submit for password change finalize form
  const onSubmit = async (values: ResetPasswordConfirmationFormValues) => {
    if (!isPasswordValid(values.password, userRequirementsInfo)) {
      return;
    }

    setSubmissionProcessing(true);
    setLastSubmittedPassword(values.password);
    setError('');
    setFormSubmitted(true);
    setRequirementOverrides(defaultRequirementOverrides);

    if (shouldShowSecurityQuestion && values.securityAnswer) {
      setLastSubmittedSecurityChallengeAnswer(values.securityAnswer);
      setSecurityChallengeError('');
    }

    const requestPayload = {
      ...(!isExpiredOrAdminPasswordReset && { changeToken: token }),
      ...(isExpiredOrAdminPasswordReset && { sessionLogout: true, siteId: group }),
      passwordSecret: values.password,
      ...(shouldShowSecurityQuestion && {
        securityChallenge: {
          answer: values.securityAnswer,
          securityQuestionType: currentSecurityQuestionType,
        },
      }),
    };

    fetch(
      `${apiRootUrl}/${isExpiredOrAdminPasswordReset ? PasswordChangeUrl : PasswordChangeFinalizeUrl}${
        isExpiredOrAdminPasswordReset ? '' : `?siteid=${group}`
      }`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(isExpiredOrAdminPasswordReset && { Authorization: token }),
        },
        body: JSON.stringify(requestPayload),
      }
    )
      .then(res => {
        if (res.status === 200) {
          res
            .json()
            .then(resBody => {
              if (resBody.ResponseCode === 0) {
                setSuccessfulSubmission(true);
              } else if (resBody.ResponseCode === 99) {
                setError(genericPasswordResetSubmissionErrorMsg);
              } else if (resBody.ResponseCode === 208) {
                setError(`${ResponseCodes[resBody.ResponseCode].description} Please contact support.`);
              } else if (resBody.ResponseCode === 115) {
                setRequirementOverrides(
                  Object.keys(defaultRequirementOverrides).reduce(
                    (requirementOverrides: RequirementOverrides, fieldRequirementType: string) => {
                      const errorMsgForFieldRequirementType: string =
                        resBody.Payload &&
                        resBody.Payload.length > 0 &&
                        resBody.Payload.find(
                          (errorMsg: string) =>
                            getFieldRequirementTypeFromErrorMessage(errorMsg) === fieldRequirementType
                        );

                      if (errorMsgForFieldRequirementType) {
                        return {
                          ...requirementOverrides,
                          [fieldRequirementType]: { requirementMet: false },
                        };
                      }

                      return {
                        ...requirementOverrides,
                        [fieldRequirementType]: { requirementMet: true },
                      };
                    },
                    { ...defaultRequirementOverrides }
                  )
                );
              } else if (resBody.ResponseCode === 209 && shouldShowSecurityQuestion) {
                setSecurityChallengeError(
                  `${ResponseCodes[resBody.ResponseCode].description} Answer the new question above.`
                );
                setLastSubmittedSecurityChallengeAnswer('');
                getSecurityChallenge();
              } else if (resBody.ResponseCode === 200) {
                if (isExpiredOrAdminPasswordReset) {
                  setError(expiredOrTempPasswordResetTokenExpiredMsg);
                } else {
                  setError(standardPasswordResetTokenExpiredMsg);
                }
              } else {
                setError(ResponseCodes[resBody.ResponseCode].description);
              }

              setSubmissionProcessing(false);
            })
            .catch(() => {
              setError(genericPasswordResetSubmissionErrorMsg);
              setSubmissionProcessing(false);
            });
        } else {
          setError(genericPasswordResetSubmissionErrorMsg);
          setSubmissionProcessing(false);
        }
      })
      .catch(() => {
        setError(genericPasswordResetSubmissionErrorMsg);
        setSubmissionProcessing(false);
      });
  };

  return (
    <div
      className={`reset-password-confirmation ${isVirtual ? 'env-virtual' : ''}`}
      style={{ fontFamily: 'Poppins, sans-serif' }}
    >
      <Header>
        <SubHeader>
          <SiteLogo siteGroup={group} />
          <nav className="nav-utilities">
            <ul>
              <li>
                {isVirtual ? (
                  <ExternalLink href={Config.goToLiveUrl + siteParam}>Go to live trading account</ExternalLink>
                ) : (
                  <TradingBlockLink to="LegacySiteHomeUrl" siteGroup={group} origin={group}>
                    &uarr; Go to <SiteName siteGroup={group} /> Legacy
                  </TradingBlockLink>
                )}
              </li>
              <li>
                <TradingBlockLink to="CustomerServiceUrl" siteGroup={group} origin={group}>
                  Contact us
                </TradingBlockLink>
              </li>
            </ul>
          </nav>
        </SubHeader>
      </Header>
      <main className="login">
        <Section>
          <div className="content">
            <div className="login-container">
              <div className="login-primary">
                <h1 className="mb-3">
                  <i aria-hidden="true" className={`fal ${isVirtual ? 'fa-vr-cardboard' : 'fa-lock-alt'}`} />{' '}
                  {isVirtual ? 'Virtual Reset Password' : 'Reset Password'}
                </h1>

                {(loadingSecurityQuestionType || submissionProcessing || loadingUserRequirements) && (
                  <div className="d-flex align-items-center justify-content-center">
                    <Loading />
                  </div>
                )}

                {!successfulSubmission &&
                  (!shouldShowSecurityQuestion || (shouldShowSecurityQuestion && currentSecurityQuestionType)) &&
                  !(loadingSecurityQuestionType || submissionProcessing || loadingUserRequirements) && (
                    <>
                      {isExpiredOrAdminPasswordReset && loginResponseCode === 113 && <p>Your password has expired.</p>}

                      {isExpiredOrAdminPasswordReset && loginResponseCode === 112 && (
                        <p>You have logged in with a temporary password.</p>
                      )}

                      <p>
                        Please {shouldShowSecurityQuestion ? 'answer the security question and then' : ''} enter and
                        confirm the new password for your {isVirtual ? 'Virtual Trading' : ''} account.
                      </p>

                      {isVirtual && <p>NOTE: Your live account login is not compatible with Virtual Trading.</p>}

                      {isExpiredOrAdminPasswordReset && <p>You have 15 minutes to reset your password.</p>}

                      {pageLoadedWithJs && (
                        <Formik
                          initialValues={
                            {
                              password: lastSubmittedPassword,
                              passwordConfirm: lastSubmittedPassword,
                              ...(shouldShowSecurityQuestion && {
                                securityAnswer: lastSubmittedSecurityChallengeAnswer,
                              }),
                            } as ResetPasswordConfirmationFormValues
                          }
                          validateOnChange
                          validationSchema={Yup.object<ResetPasswordConfirmationFormValues>().shape({
                            password: Yup.string()
                              .trim()
                              .required('Password is required.'),
                            passwordConfirm: Yup.string()
                              .trim()
                              .required('Password Confirmation is required.')
                              .oneOf([Yup.ref('password')], 'Passwords do not match.'),
                            ...(shouldShowSecurityQuestion && {
                              securityAnswer: Yup.string()
                                .trim()
                                .required('Security Answer is required.'),
                            }),
                          })}
                          onSubmit={onSubmit}
                        >
                          {({ values }) => (
                            <Form translate={undefined}>
                              <>
                                {shouldShowSecurityQuestion && (
                                  <>
                                    <Text
                                      id={`securityChallenges.securityQuestionType.${currentSecurityQuestionType}`}
                                      type="field"
                                    />
                                    <FormGroup
                                      className={
                                        securityChallengeError ? 'password-reset-form-group-security-answer-field' : ''
                                      }
                                    >
                                      <Field
                                        component={TextboxField}
                                        id="securityAnswer"
                                        placeholder="Security Answer"
                                      />
                                    </FormGroup>
                                  </>
                                )}
                                <FormGroup
                                  className="password-reset-form-group-password-field"
                                  help={
                                    <FieldRequirementsHelp
                                      field="password"
                                      formSubmitted={formSubmitted}
                                      userRequirementsInfo={userRequirementsInfo}
                                      context="resetPassword"
                                      requirementOverrides={requirementOverrides}
                                      values={{ password: values.password }}
                                    />
                                  }
                                >
                                  <Field
                                    type="password"
                                    component={TextboxField}
                                    id="password"
                                    placeholder="New Password"
                                    allowUnmask
                                    autoComplete="new-password"
                                  />
                                </FormGroup>
                                <FormGroup>
                                  <Field
                                    type="password"
                                    component={TextboxField}
                                    id="passwordConfirm"
                                    placeholder="Confirm New Password"
                                    allowUnmask
                                  />
                                </FormGroup>
                              </>

                              <button
                                className={`btn btn-block btn-light btn-icon ${isVirtual ? 'btn-virtual' : ''}`}
                                type="submit"
                              >
                                Submit
                                <span className="tb-icon">
                                  {' '}
                                  {isVirtual ? <i className="fal fa-vr-cardboard" /> : <Svg path="icon-arrow" />}
                                </span>
                              </button>
                            </Form>
                          )}
                        </Formik>
                      )}
                    </>
                  )}

                {successfulSubmission && (
                  <>
                    <p>Your password has been reset.</p>
                    <Link
                      to={`/?grp=${group}`}
                      className={`forgot-password-login-link btn btn-block btn-light btn-icon ${
                        isVirtual ? 'btn-virtual' : ''
                      }`}
                    >
                      Log in
                      <span className="tb-icon">
                        {isVirtual ? <i className="fal fa-vr-cardboard" /> : <Svg path="icon-arrow" />}
                      </span>
                    </Link>
                  </>
                )}

                {(error || securityChallengeError) &&
                  !(loadingSecurityQuestionType || submissionProcessing || loadingUserRequirements) && (
                    <p className="error" style={{ marginBottom: 0, marginTop: '1rem' }}>
                      {error || securityChallengeError}
                    </p>
                  )}

                <noscript>
                  <div className="alert alert-danger">
                    <h4 className="error" style={{ marginBottom: 0 }}>
                      Javascript is required for this site.
                    </h4>
                  </div>
                </noscript>

                <LoginCardAuthFooter
                  isVirtual={isVirtual}
                  group={group}
                  siteParam={siteParam}
                  environmentConfig={config}
                  recovery={
                    <Link className="login-primary-recovery-link" to={grp ? `/?grp=${grp}` : '/'}>
                      Log in
                    </Link>
                  }
                />
              </div>

              <div className="login-secondary">
                <img alt="" aria-hidden="true" src={getStaticPath('/images/sign-in-hero.png')} />
              </div>
            </div>
          </div>
        </Section>
      </main>
      <footer className="footer">
        <div className="content">
          <FooterTeaser siteGroup={group} />
        </div>
      </footer>
    </div>
  );
};
